import Vue from "vue";
import Vuex from "vuex";
import router from "../router";
import Parse, { parseAppId, parseServerUrl } from "../configs/parse";
import utils from "../utils/index";
import axios from "../configs/axios";
import { getSessionToken } from "../utils/cookie";

Vue.use(Vuex);

const state = {
  PARSE_URL: parseServerUrl,
  PARSE_APP_ID: parseAppId,
  supportPhone: "09120045303",

  bookmarkedLoads: null,
  ownerId: null,

  showSnackbar: false,
  snackbarOptions: {
    color: "",
    mode: "",
    multiLine: false,
    timeout: 6000,
    right: false,
    bottom: true,
    left: false,
    top: false,
    vertical: false,
    text: "",
    actionBtn: true,
    actionSlotName: "",
  },

  user: null,
  isLaunched: false,
  configsChecked: false,
  loginRedirect: "home",

  plan: null,
  userPremiumSubscription: null,
  loadingUserSubscription: false,
};

const mutations = {
  toggle_launcher(state, isLaunched) {
    if (typeof isLaunched == "boolean") state.isLaunched = isLaunched;
  },
  set_redirect(state, routeName) {
    if (typeof routeName == "string") state.loginRedirect = routeName;
  },
  set_user(state, user) {
    state.user = user ? user : null;
  },
  set_premium_subscription(state, subscription) {
    state.userPremiumSubscription = subscription ? subscription : null;
  },
  log_out(state, userExist = true) {
    if (userExist) {
      Parse.User.logOut()
        .then((res) => {
          if (res) router.push({ name: "login" }).catch(() => {});
        })
        .catch((e) => utils.handleErrorIfKnown(e?.message));
    } else {
      // remove stored user from localstorage and parse internal storage
      localStorage.removeItem(`Parse/${state.PARSE_APP_ID}/currentUser`);
      Parse.User._clearCache();

      router.push({ name: "login" }).catch(() => {});
    }
  },
  show_snackbar(state, options) {
    const snackbarDefaultOptions = {
      color: "dark",
      multiLine: false,
      timeout: 3000,
      right: false,
      bottom: true,
      left: false,
      top: false,
      vertical: false,
      text: "",
      actionBtn: true,
      actionSlotName: "",
    };

    state.snackbarOptions = Object.assign(snackbarDefaultOptions, options);
    state.showSnackbar = false;
    setTimeout(() => {
      state.showSnackbar = true;
    }, 0);
  },
  remove_bookmarked_load(state, loadId) {
    state.bookmarkedLoads = state.bookmarkedLoads.filter(
      (item) => item.objectId != loadId
    );

    const bookmarks = JSON.stringify(state.bookmarkedLoads);
    localStorage.setItem("bookmarkedLoads", bookmarks);
  },
  set_owner_id(state, val) {
    state.ownerId = val;
  },
};

const actions = {
  get_configs({ state }) {
    return new Promise(async (resolve, reject) => {
      if (state.configsChecked) return resolve("launcher ran onetime");

      const uri = "get_configs_for_driver";
      const configs = await Parse.Cloud.run(uri, { vcode: 0 }).catch(reject);

      // TODO check if there is value if catches in next if statement

      if (!configs) return;

      const store = (key, value) => {
        localStorage.setItem(key, JSON.stringify(value));
      };

      store("Configs", configs);

      const promises = [];
      const configChecker = (name) => {
        const config = configs.find(
          (config) => config.get("name") == name.toLowerCase()
        );
        if (!config) return reject();

        const currValue = localStorage.getItem(name);
        const currVersion = localStorage.getItem(`${name}V`);
        const newVersion = config.get("vcode") ?? Infinity;

        if (!currValue || !currVersion || currVersion < newVersion) {
          localStorage.removeItem(`${name}V`);
          localStorage.removeItem(name);

          const uri = config.get("content_link");
          const promise = axios.get(uri).then((res) => {
            store(name, res.data);
            store(`${name}V`, newVersion);
          });

          promises.push(promise);
        }
      };

      ["Places", "Vehicles"].forEach((name) => configChecker(name));

      Promise.all(promises)
        .then(() => {
          state.configsChecked = true;
          resolve(configs);
        })
        .catch((err) => reject(err));
    });
  },
  get_premium_subscription({ state, commit }) {
    state.loadingUserSubscription = true;
    commit("set_premium_subscription", null);

    return new Promise((resolve, reject) => {
      Parse.Cloud.run("get_user_premium_subscription_v2")
        .then((res) => {
          if (!res) return reject();

          commit("set_premium_subscription", res.subscription);
          state.plan = res.plan;

          resolve(res);
        })
        .catch((err) => {
          if (err?.message === 4009) {
            resolve();
          } else {
            reject(err);
          }
        })
        .finally(() => {
          state.loadingUserSubscription = false;
        });
    });
  },
  become_user({ commit }) {
    return new Promise(async (resolve, reject) => {
      const sessionToken = getSessionToken();
      if (!sessionToken) return reject();

      try {
        let user = await Parse.User.become(sessionToken);
        if (!user) return reject();

        user = JSON.parse(JSON.stringify(user));
        commit("set_user", user);
        resolve(user);
      } catch (e) {
        reject(e);
      }
    });
  },
  fetch_user({ commit }) {
    return new Promise((resolve, reject) => {
      const localUser = Parse.User.current();
      if (!localUser) reject("user not found");

      Parse.Cloud.run("fetch_user_detail")
        .then(async (user) => {
          if (!user) return reject("cannot fetch user");

          user = JSON.parse(JSON.stringify(user) ?? null);
          user.sessionToken = localUser.get("sessionToken");

          await Parse.User.hydrate(user); // update user in parse internal storage and localStorage
          commit("set_user", user);

          resolve(user);
        })
        .catch(reject);
    });
  },
  bookmark_load({ state }, load) {
    return new Promise((resolve, reject) => {
      const sameId = (item) => item.objectId === load.objectId;
      const exists = state.bookmarkedLoads.some(sameId);
      if (exists) return reject("already bookmarked");

      state.bookmarkedLoads.push(load);

      const bookmarks = JSON.stringify(state.bookmarkedLoads);
      localStorage.setItem("bookmarkedLoads", bookmarks);

      resolve("done");
    });
  },
  load_panel_user({ dispatch }) {
    return new Promise((resolve, reject) => {
      dispatch("fetch_user")
        .then((user) => {
          if (!user) return reject("cannot fetch user");

          resolve(user);
          // dispatch("get_premium_subscription")
          //   .then(() => resolve(user))
          //   .catch((err) => utils.handleErrorIfKnown(err?.message));
        })
        .catch((err) => {
          reject(err);
          utils.handleErrorIfKnown(err?.message);
        });
    });
  },
};

const getters = {
  userData: (state) => JSON.parse(JSON.stringify(state.user)),
  isLaunched: (state) => state.isLaunched,
  redirect: (state) => state.loginRedirect,
  userPremiumSubscription: (state) => state.userPremiumSubscription,
  loadingUserSubscription: (state) => state.loadingUserSubscription,
};

export default new Vuex.Store({
  state,
  mutations,
  actions,
  getters,
});
